import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PdmApi } from '../../api/PdmApi';
import { ProcosApi } from '../../api/ProcosAPi';
import { preparePropertiesLov } from '../../services/DataHelperFunctions';

import type { IPagination, IReadFeatures } from '../../../types/IRequestTypes';
import type { IFeature, ILov, IPdmState, IProductHierarchy, IProperty } from '../../../types';

const initialState: IPdmState = {
  features: [],
  properties: [],
  listOfValues: [],
  productHierarchy: [],
  isModified: false,
};

export const getFeatures = createAsyncThunk('pdm/getFeatures', async (payload: IReadFeatures) => {
  return await ProcosApi.getFeatures(payload);
});

export const getProperties = createAsyncThunk('pdm/getProperties', async (payload: IPagination) => {
  return await PdmApi.getProperties(payload);
});

export const getListOfValues = createAsyncThunk('pdm/getListOfValues', async () => {
  return await PdmApi.getListOfValues();
});

export const getProductHierarchy = createAsyncThunk('pdm/getProductHierarchy', async () => {
  return await PdmApi.getProductHierarchy();
});

const PdmSlice = createSlice({
  name: 'pdm',
  initialState,
  reducers: {
    setIsDataUpdated(state, action) {
      state.isModified = action.payload;
    },
    resetToInitial(state) {
      state.features = initialState.features;
      state.isModified = false;
      state.listOfValues = initialState.listOfValues;
      state.properties = initialState.properties;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeatures.fulfilled, (state, action) => {
        if ('error' in action.payload) return;
        state.features = action.payload.data.filter((feature: IFeature) => feature.code !== 'PMP');
      })
      .addCase(getProperties.fulfilled, (state, action) => {
        if ('error' in action.payload) return;
        const { properties } = action.payload.data as { properties: IProperty[] };
        const filtered = properties.filter((property) => property.origin === 'PDM');
        state.properties = filtered.toSorted((a, b) => a.type.localeCompare(b.type));
      })
      .addCase(getListOfValues.fulfilled, (state, action) => {
        if ('error' in action.payload) return;
        const lovData = action.payload.data as ILov[];
        state.listOfValues = lovData;
        state.properties = preparePropertiesLov(state.properties, state.productHierarchy, lovData);
      })
      .addCase(getProductHierarchy.fulfilled, (state, action) => {
        if ('error' in action.payload) return;
        state.productHierarchy = action.payload.data as IProductHierarchy[];
      });
  },
});

export const { setIsDataUpdated, resetToInitial } = PdmSlice.actions;

export default PdmSlice.reducer;
